// © Microsoft Corporation. All rights reserved.

import { MediatorEventNames } from "./MediatorEventNames";
import { PrimeIntegrationConstants } from "./Constants";
import { SubnavTabId } from "./SubnavTabId";
import { TabsHelper } from "./SubnavTabHelper";
import { getMediator } from "mediator-integration";
import { logger } from "@msnews/diagnostics";

export namespace Mediator {
    /**
     * API to listen for tab change event from Prime
     *
     * @export
     * @param callback {(tabId: number) => void} - A callback method to handle the event
     * @param shouldCallTabChangeEventImmediately {Boolean} - if we should immediately call the callback in order to capture any changes between initialization and registration.
     * @return Promise<void>
     */
    export async function registerForTabChangeEvent(callback: (tabId: number) => void, shouldCallCallbackImmediately = false): Promise<void> {
        try {
            const mediator = await getMediator();

            function sendActiveTabToCallback(data: PrimeGlobal.INavigateEventData): void {
                let activeTab: SubnavTabId = getActiveTabValue();

                // This is only used if the caller of the mediator event neglected to set window.navigation and if the caller decides to use "tab" instead of "tabId"
                if (activeTab === SubnavTabId.None && data && data.tab) {
                    activeTab = data.tab;
                }

                if (window._perfMarker) {
                    window._perfMarker("TimeToTabClick_" + TabsHelper.tabIdToTabName(activeTab), true);
                }

                callback(activeTab);
            }

            mediator.sub(MediatorEventNames.TabNavigation, sendActiveTabToCallback);
            if (shouldCallCallbackImmediately) {
                sendActiveTabToCallback(null);
            }
        } catch {
            logger.logError("Couldn't load the mediator object.");
        }
    }

    /**
     * API to listen for content actions updates from Prime
     *
     * @export
     * @param callback {()) => void} - A callback method to handle the event
     * @return Promise<void>
     */
    export async function registerContentActionsUpdatedEvent(callback: () => void): Promise<void> {
        try {
            const mediator = await getMediator();
            mediator.sub(MediatorEventNames.ContentActionsUpdated, callback);
        } catch {
            logger.logError("Couldn't load the mediator object.");
        }
    }

    /**
     * API to listen for card action enable event from Prime
     *
     * @export
     * @param callback {(initData PrimeGlobal.ICardActionInitEventData)) => void} - A callback method to handle the event
     * @return Promise<void>
     */
    export async function registerCardActionEnableEvent(callback: (initData: PrimeGlobal.ICardActionInitEventData) => void): Promise<void> {
        try {
            const mediator = await getMediator();
            mediator.sub(MediatorEventNames.CardActionInit, (initData: PrimeGlobal.ICardActionInitEventData): void => {
                callback(initData);
            });
        } catch {
            logger.logError("Couldn't load the mediator object.");
        }
    }

    /**
     * API to listen for card action post event from Prime
     *
     * @export
     * @param callback {(initData PrimeGlobal.ICardActionPostEventData)) => void} - A callback method to handle the event
     * @return Promise<void>
     */
    export async function registerCardActionPostEvent(callback: (postData: PrimeGlobal.ICardActionPostEventData) => void): Promise<void> {
        try {
            const mediator = await getMediator();
            mediator.sub(MediatorEventNames.CardActionPost, (postData: PrimeGlobal.ICardActionPostEventData): void => {
                callback(postData);
            });
        } catch {
            logger.logError("Couldn't load the mediator object.");
        }
    }

    /**
     * API to listen for follow button click event from Prime
     *
     * @export
     * @param callback {(topicId: string, isFollowed: boolean) => void} - A callback method to handle the event
     * @return Promise<void>
     */
    export async function registerForFollowButtonClickEvent(callback: (topicId: string, isFollowed: boolean) => void): Promise<void> {
        try {
            const mediator = await getMediator();
            mediator.sub(MediatorEventNames.TopicAction, function (followButtonData: PrimeGlobal.IFollowButtonClickData): void {
                if (followButtonData && followButtonData.topicId) {
                    callback(followButtonData.topicId, followButtonData.isFollowed);
                }
            });
        } catch {
            logger.logError("Couldn't load the mediator object.");
        }
    }

    /**
     * API to listen for Interests render complete event.
     *
     * @export
     * @return Promise<void>
     */
    export async function registerForInterestsRenderCompleteEvent(callback: () => void): Promise<void> {
        try {
            const mediator = await getMediator();
            mediator.sub(MediatorEventNames.InterestsRenderComplete, function (): void {
                callback();
            });
        } catch {
            logger.logError("Couldn't load the mediator object.");
        }
    }

    /**
     * Returns the active tab
     * @public
     * @return {SubnavTabId}
     */
    export function getActiveTabValue(): SubnavTabId {
        let activeTabValue = "";

        if (window.navigation && typeof window.navigation.activeTab === "string") {
            activeTabValue = window.navigation.activeTab.toLowerCase();
        }

        return TabsHelper.tabNameToTabId(activeTabValue);
    }

    /**
     * API to publish the event to Prime.
     *
     * @export
     * @param event - the mediator event name string
     * @param data - the event data
     * @return Promise<void>
     */
    export async function publishEventForPrime(event: string, data?: any): Promise<void> {
        try {
            const mediator = await getMediator();
            mediator.pub(event, data);
        } catch {
            logger.logError("Couldn't load the mediator object in publishEventForPrime.");
        }
    }

    /**
     * Publishes a message to trigger rendering of a mediated coach mark from Prime page
     * @param refElement {Element} - the reference element next to which the coachmark should be rendered. This is optional for Overlay type of coachmarks
     * @param hideCoachMark {boolean} - flag to indicate whether to hide the coachmark. Default value of false indicates that the coachmark should be shown.
     * @returns Promise{void} - The promise that will return after the action has been completed.
     */
    export async function renderUserActionErrorCoachmark(
        args: { refElement?: Element, hideCoachMark?: boolean } = {}): Promise<void> {

        args = {
            refElement: undefined,
            hideCoachMark: false,
            ...args
        };

        const mediator = await getMediator();
        mediator.pub(
            MediatorEventNames.MediatedCoachMark,
            {
                coachMarkId: PrimeIntegrationConstants.userActionErrorCoachMarkId,
                refElement: args.refElement,
                hideCoachMark: args.hideCoachMark
            });
    }
}